export interface MiddlewareRequestContext {
  getUrl: () => string;
  setHeaderParam: (key: string, value: string) => void;
}

export interface MiddlewareResponseContext {
  httpStatusCode: number;
}

interface SdkMiddleware<
  RequestContext extends MiddlewareRequestContext,
  ResponseContext extends MiddlewareResponseContext,
> {
  pre: (context: RequestContext) => Promise<RequestContext>;
  post: (context: ResponseContext) => Promise<ResponseContext>;
}

interface SharedRefreshTokenParams {
  authToken: string;
  baseUrl: string;
  updateAuthToken: (token: string) => void;
  useRefreshToken: boolean;
  refreshTokenIfNeeded: (
    authToken: string,
    baseUrl: string,
    updateAuthToken: (token: string) => void
  ) => Promise<string>;
}

export const createSharedRefreshToken = ({
  authToken,
  baseUrl,
  updateAuthToken,
  useRefreshToken,
  refreshTokenIfNeeded,
}: SharedRefreshTokenParams) => {
  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;

  return () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(
      authToken,
      baseUrl,
      updateAuthToken
    ).then(
      (token) => {
        isRefreshing = false;
        return token;
      },
      () => {
        isRefreshing = false;
        return authToken;
      }
    );
    return refreshPromise;
  };
};

export const createAuthMiddleware = <
  RequestContext extends MiddlewareRequestContext,
  ResponseContext extends MiddlewareResponseContext,
>(
  refreshToken: () => Promise<string>,
  serverFlags: string | null,
  useRefreshToken: boolean
): SdkMiddleware<RequestContext, ResponseContext> => ({
  pre: async (context) => {
    if (serverFlags) {
      context.setHeaderParam('X-Server-Flags', serverFlags);
    }

    if (!context.getUrl().endsWith('/refresh')) {
      const newAuthToken = await refreshToken();
      context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
    }

    return context;
  },
  post: async (context) => {
    if (useRefreshToken && context.httpStatusCode === 401) {
      location.href = '/logout';
    }
    return context;
  },
});
